import React, { useState, useEffect, useCallback } from "react";
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { helperService } from "../../../services/helper.service";
import { apiService } from "../../../services/api.service";

import { IUser, IAddress, IDogCoownerInfo, UserUpdateDto } from "@eagerdog/interfaces";

import Input from "src/components/Input/Input";
import Phone from "src/components/Phone/Phone";
import Dropdown, { IOption, States, Provinces } from "src/components/Dropdown/Dropdown";

import flagIcon from "src/assets/icons/flag.svg";

import "./UserAddress.scss";

interface IProps {
  user: IUser | IDogCoownerInfo | UserUpdateDto,
  onChange(address: IAddress, phone: string): void,
  onBehalf?: boolean,
  onBehalfUserId?: string,
  required?: boolean,
  hidePhone?: boolean,
  default?: IAddress
}

export interface IAddressError {
  valid: boolean,
  message?: string
}

export const isValidPhone = (phone: string | undefined) => {
  if (phone !== undefined && !isPossiblePhoneNumber(phone)) {
    return { valid: false, message: "Please enter a valid phone number" };
  }

  return { valid: true };
}

// Countries where we show a proper State/Province dropdown (with format-validated
// postal codes below) and require it to be filled in. Every other country gets a
// free-text, optional "State / Province / Region" field instead - most countries
// don't have a fixed, well-known list of subdivisions the way the US/Canada do.
export const COUNTRIES_REQUIRING_STATE = ["USA", "Canada"];

export const isValidAddress = (address: IAddress | undefined) => {
  if (address === undefined ||
      address.city?.length === 0 ||
      address.country?.length === 0 ||
      address.line1?.length === 0 ||
      address.postal_code?.length === 0 ||
      (COUNTRIES_REQUIRING_STATE.includes(address.country || "") && address.state?.length === 0)) {
    return { valid: false, message: "Please enter a valid handler address" };
  }

  if (address.country === "Canada" && address.postal_code !== undefined) {
    let regex = new RegExp(/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/);

    if (!regex.test(address.postal_code)) {
      return { valid: false, message: "Please enter a valid handler postal code" };
    }
  } else if (address.country === "USA" && address.postal_code !== undefined) {
    let regex = new RegExp(/^\d{5}(?:[-\s]\d{4})?$/);

    if (!regex.test(address.postal_code)) {
      return { valid: false, message: "Please enter a valid handler zip code" };
    }
  }

  return { valid: true };
}

// Resolves a stored state/province string into a dropdown option when it matches a
// known US state or Canadian province code, or falls back to treating it as free-text
// (e.g. a UK county) rather than silently defaulting to an unrelated province.
const resolveStateOption = (stateValue?: string): IOption => {
  if (!stateValue) return { value: "Select a State", id: "" };
  return (
    Provinces.find((o) => o.id === stateValue) ||
    States.find((o) => o.id === stateValue) ||
    { value: stateValue, id: stateValue }
  );
};

const UserAddress: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [changesMade, setChangesMade] = useState<boolean>(false);

  const [line1, setLine1] = useState<string>(props.default?.line1 || "");
  const [line2, setLine2] = useState<string>(props.default?.line2 || "");
  const [city, setCity] = useState<string>(props.default?.city || "");
  const [country, setCountry] = useState<IOption>(props.default?.country ? { value: props.default?.country, id: props.default.country } : { value: "Select a Country", id: "" });
  const [state, setState] = useState<IOption>(resolveStateOption(props.default?.state));
  const [postalCode, setPostalCode] = useState<string>(props.default?.postal_code || "");
  const [phone, setPhone] = useState<string>("");

  // Use ref to always have the latest onChange callback without triggering re-renders
  const onChangeRef = React.useRef(props.onChange);
  React.useEffect(() => {
    onChangeRef.current = props.onChange;
  }, [props.onChange]);

  const hydrateUserData = useCallback(async () => {
    if (props.onBehalf === false || props.onBehalf === undefined) {
      setLine1(props.user?.address?.line1 || "");
      setLine2(props.user?.address?.line2 || "");
      setCity(props.user?.address?.city || "");
      setCountry(props.user?.address?.country ? { id: props.user?.address?.country, value: props.user?.address?.country} : { value: "Select a Country", id: "" });
      setState(resolveStateOption(props.user?.address?.state));
      setPostalCode(props.user?.address?.postal_code || "");
      setPhone(props.user?.phone || "");
    } else if (props.onBehalf === true && props.onBehalfUserId) {
      // Load address information for on-behalf registrations
      try {
        const onBehalfUser = await apiService.getUserById(props.onBehalfUserId);
        setLine1(onBehalfUser?.address?.line1 || "");
        setLine2(onBehalfUser?.address?.line2 || "");
        setCity(onBehalfUser?.address?.city || "");
        setCountry(onBehalfUser?.address?.country ? { id: onBehalfUser?.address?.country, value: onBehalfUser?.address?.country} : { value: "Select a Country", id: "" });
        setState(resolveStateOption(onBehalfUser?.address?.state));
        setPostalCode(onBehalfUser?.address?.postal_code || "");
        setPhone(onBehalfUser?.phone || "");
      } catch (error) {
        console.error("Error loading user address for on-behalf registration:", error);
        // If error loading user data, leave fields empty
      }
    }
  }, [props.onBehalf, props.onBehalfUserId, props.user]);

  useEffect(() => {
    // Hydrate on initial load only
    if (!loaded && !props.default) {
      hydrateUserData().then(() => {
        setLoaded(true);
      });
    }
  }, [loaded, props.default]); // Removed hydrateUserData from dependencies

  // Re-hydrate whenever the user prop changes (after initial load)
  // Only re-hydrate if we're switching between onBehalf users
  useEffect(() => {
    if (loaded && !props.default && props.onBehalf && props.onBehalfUserId) {
      hydrateUserData();
    }
  }, [props.onBehalfUserId, loaded, props.default]); // Removed hydrateUserData from dependencies

  useEffect(() => {
    if (changesMade) {
      onChangeRef.current({
        line1, 
        line2, 
        city,
        state: state.id, 
        country: country.id, 
        postal_code: postalCode,
      }, phone);

      setChangesMade(false);
    }
  }, [changesMade, line1, line2, city, country, state, postalCode, phone]);

  // This is the only place that should clear `state` on a country change. The Country
  // Dropdown's onChange intentionally does NOT do this itself: Dropdown re-fires onChange
  // whenever its value prop is updated programmatically (e.g. right after hydration sets
  // country/state together), which would otherwise wipe out a just-loaded state value
  // before the user ever touched the field. Comparing against the previous country here
  // ensures we only clear state on a genuine change.
  const pCountry = helperService.usePrevious(country.id);

  useEffect(() => {
    if (loaded && pCountry !== "" && pCountry !== country.id) {
      setState({ value: "Select a State", id: "" })
    }
  }, [loaded, country, pCountry, setState]);

  return (
    <div className="UserAddress">
      <div className="addressWrap">
        <div className="inputRow">
          <Input required={props.required !== undefined ? props.required : true} label="Address Line 1" id="line1" type="text" onChange={(e) => { e.stopPropagation(); setChangesMade(true);  setLine1(e.target.value); }} value={line1} placeholder="Address Line 1" />
          <Input label="Line 2" id="line2" type="text" onChange={(e) => { e.stopPropagation(); setChangesMade(true);  setLine2(e.target.value); }} value={line2} placeholder="Line 2" />
        </div>
        <div className="inputRow">
          <Input label="City" id="city" required={props.required !== undefined ? props.required : true} type="text" onChange={(e) => { e.stopPropagation(); setChangesMade(true);  setCity(e.target.value); }} value={city} placeholder="City" />
          <Input label="Postal/Zip Code" required={props.required !== undefined ? props.required : true} id="postalCode" type="text" onChange={(e) => { e.stopPropagation(); setChangesMade(true);  setPostalCode(e.target.value); }} value={postalCode} placeholder="Postal Code" /> 
        </div>
        <div className="inputRow">
          <Dropdown value={country} onChange={(e: any, value: IOption) => { setChangesMade(true); setCountry(value); }} icon={flagIcon} label="Country" options={[{ value: "USA", id: "USA" }, { value: "Canada", id: "Canada" }, { value: "United Kingdom", id: "United Kingdom" }]} placeholder="Choose your country of residence" />
          {country.value === "USA" && <Dropdown value={state} onChange={(e: any, value: IOption) => { setState(value); setChangesMade(true); }} icon={flagIcon} label="State" options={States} placeholder="Choose your state of residence" />}
          {country.value === "Canada" && <Dropdown value={state} onChange={(e: any, value: IOption) => { setState(value); setChangesMade(true); }} icon={flagIcon} label="Province" options={Provinces} placeholder="Choose your province of residence" />}
          {country.value !== "USA" && country.value !== "Canada" && <Input label="State / Province / Region" id="stateRegion" type="text" onChange={(e) => { e.stopPropagation(); setChangesMade(true); setState({ value: e.target.value, id: e.target.value }); }} value={state.id} placeholder="State / Province / Region (optional)" />}
        </div>
        {!props.hidePhone && <div className="inputRow">
          <Phone key={`phone-${loaded}`} label="Phone Number" required={props.required !== undefined ? props.required : true} value={phone} onChange={(phone: any) => { setPhone(phone); setChangesMade(true);  }} />
        </div>}
      </div>
    </div>
  );
};

export default UserAddress;
